import React from 'react';
import {
  Box,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import type {
  ScriptStep,
  UserInputMode,
} from '@shared/interfaces/assistantScripts';
import { SectionLabelText } from '../AssistantScriptDialog.styled';
import { itemNames } from '../multiSelect';

interface UserInputControlProps {
  step: ScriptStep;
  stepUserInputMode: UserInputMode;
  onModeChange: (mode: UserInputMode) => void;
  onUpdateStep: (data: Partial<ScriptStep>) => void;
}

const UserInputControl = ({
  step,
  stepUserInputMode,
  onModeChange,
  onUpdateStep,
}: UserInputControlProps) => {
  const { t } = useTranslation('common');

  return (
    <>
      <Divider sx={{ borderColor: 'var(--am-border)' }} />
      <Box>
        <SectionLabelText>{t('assistant.scriptDialog.userInputControl')}</SectionLabelText>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              sx={{
                fontSize: '14px',
                color: 'var(--am-text-primary)',
                whiteSpace: 'nowrap',
                minWidth: '120px',
              }}
            >
              {t('assistant.scriptDialog.waitForUserInput')}
            </Typography>
            <FormControl sx={{ width: '200px' }}>
              <Select
                size="small"
                value={stepUserInputMode}
                onChange={(e) => onModeChange(e.target.value as UserInputMode)}
              >
                <MenuItem value="none">{t('assistant.scriptDialog.userInputNone')}</MenuItem>
                <MenuItem value="button_next">
                  {t('assistant.scriptDialog.userInputButtonNext')}
                </MenuItem>
                <MenuItem value="click_anywhere">{t('assistant.scriptDialog.userInputClickAnywhere')}</MenuItem>
                <MenuItem value="keyboard">{t('assistant.scriptDialog.userInputKeyboard')}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {stepUserInputMode === 'keyboard' && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                sx={{
                  fontSize: '14px',
                  color: 'var(--am-text-primary)',
                  whiteSpace: 'nowrap',
                  minWidth: '120px',
                }}
              >
                {t('assistant.scriptDialog.key')}
              </Typography>
              <FormControl sx={{ width: '200px' }}>
                <Select
                  multiple
                  size="small"
                  value={step.allowedKeys ?? []}
                  onChange={(e) =>
                    onUpdateStep({ allowedKeys: e.target.value as string[] })
                  }
                  displayEmpty
                  renderValue={(selected) =>
                    (selected as string[]).length === 0 ? (
                      <Typography
                        sx={{
                          fontSize: '14px',
                          color: 'var(--am-text-disabled)',
                        }}
                      >
                        {t('assistant.scriptDialog.selectKey')}
                      </Typography>
                    ) : (
                      (selected as string[]).join(', ')
                    )
                  }
                >
                  {itemNames.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default UserInputControl;
